<div class="price-upload-error-modal">
  <!-- Header with Gradient matching ImportFailModal -->
  <div class="modal-header">
    <h5 class="modal-title">Price Upload Errors</h5>
    <button type="button" class="close" aria-label="Close" (click)="onClose()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body">
    <!-- Success count banner -->
    <div *ngIf="totalCount > 0" class="alert alert-success d-flex align-items-center mb-0">
      <i class="fas fa-check-circle mr-2"></i>
      <span><strong>{{ totalCount }}</strong> item(s) updated successfully.</span>
    </div>

    <!-- Section 1: Troubleshooting Guide -->
    <div class="content-card p-0">
      <div class="guide-wrapper">
        <div class="guide-header" (click)="toggleGuide()">
          <i class="fas fa-info-circle"></i>
          <span>Understanding & Fixing High Price Errors</span>
          <i class="fas ml-auto" [ngClass]="isGuideCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
        </div>

        <div [ngbCollapse]="isGuideCollapsed">
          <div class="guide-content">
            <p>
              A high price error occurs when Amazon detects that your listing price may be incorrectly set too high, potentially due to a pricing mistake or policy violation. Here's what you need to know:
            </p>

            <div class="guide-columns">
              <div class="guide-col">
                <h6 class="causes-title">Common Causes</h6>
                <ul>
                  <li><strong>Decimal point errors</strong> - e.g., entering $1000.00 instead of $100.00 <a href="https://sellercentral.amazon.com/help/hub/reference/GVJKU2LH6G39A2NE" target="_blank" rel="noopener noreferrer" class="ext-link">[➚]</a></li>
                  <li><strong>Typographical mistakes</strong> - Extra zeros or incorrect price entry <a href="https://sellercentral.amazon.com/help/hub/reference/GVJKU2LH6G39A2NE" target="_blank" rel="noopener noreferrer" class="ext-link">[➚]</a></li>
                  <li><strong>Price exceeds thresholds</strong> - Substantially higher than historical data or Amazon's limits <a href="https://sellercentral.amazon.com/help/hub/reference/GVJKU2LH6G39A2NE" target="_blank" rel="noopener noreferrer" class="ext-link">[➚]</a></li>
                  <li><strong>Business price issues</strong> - Business prices set higher than regular offers <a href="https://sellercentral.amazon.com/help/hub/reference/GVJKU2LH6G39A2NE" target="_blank" rel="noopener noreferrer" class="ext-link">[➚]</a></li>
                </ul>
              </div>
              <div class="guide-col">
                <h6 class="fixes-title">How to Fix It</h6>
                <ul>
                  <li><strong>Review pricing carefully</strong> - Check for typos and decimal errors <a href="https://sellercentral.amazon.com/help/hub/reference/GVJKU2LH6G39A2NE" target="_blank" rel="noopener noreferrer" class="ext-link">[➚]</a></li>
                  <li><strong>Check all price types</strong> - Review selling, discounted, deal, and business prices <a href="https://sellercentral.amazon.com/help/hub/reference/GVJKU2LH6G39A2NE" target="_blank" rel="noopener noreferrer" class="ext-link">[➚]</a></li>
                  <li><strong>Update incorrect prices</strong> - Adjust to fall below the threshold <a href="https://sellercentral.amazon.com/help/hub/reference/GVJKU2LH6G39A2NE" target="_blank" rel="noopener noreferrer" class="ext-link">[➚]</a></li>
                  <li><strong>Access pricing issues</strong> - Go to Inventory > Manage All Inventory > Activate Products > "Pricing Issues" <a href="https://sellercentral.amazon.com/help/hub/reference/G201686950" target="_blank" rel="noopener noreferrer" class="ext-link">[➚]</a></li>
                </ul>
              </div>
            </div>

            <!-- Important Notes Section -->
            <div class="notes-box">
              <h6>Important Notes & Alternative Methods</h6>
              <ul>
                <li>Alternatively, navigate to <strong>Pricing menu > Pricing Health</strong> and click "Show products discontinued due to possible pricing errors". <a href="https://sellercentral.amazon.com/help/hub/reference/G201686950" target="_blank" rel="noopener noreferrer" class="ext-link">[➚]</a></li>
                <li>If you set the same or similar price after correction, the product may remain suppressed. <a href="https://sellercentral.amazon.com/help/hub/reference/G201686950" target="_blank" rel="noopener noreferrer" class="ext-link">[➚]</a></li>
                <li>Consider using <strong>Automate Pricing rules</strong> to match competitive prices automatically. <a href="https://sellercentral.amazon.com/help/hub/reference/G201686950" target="_blank" rel="noopener noreferrer" class="ext-link">[➚]</a></li>
                <li>For multiple products, use inventory loader or price and quantity data feeds for bulk updates. <a href="https://sellercentral.amazon.com/help/hub/reference/G201686950" target="_blank" rel="noopener noreferrer" class="ext-link">[➚]</a></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 2: Error List -->
    <ng-container *ngIf="!isLoading; else loadingTpl">
      <div class="content-card content-card--error-list">
        <ul class="error-list d-flex flex-column gap-3">
          <li *ngFor="let error of errors" class="error-item">
            <div class="bullet-point">
              <i class="fas fa-circle"></i>
            </div>
            <div class="error-text">
              <span class="line-number mr-1" *ngIf="error.lineNumber"><strong>Line {{ error.lineNumber }}</strong> &nbsp;|</span>
              <a [href]="error.amazonLink" target="_blank" rel="noopener noreferrer" class="amazon-link inline-link mr-1" *ngIf="error.amazonLink">
                [
                <span>Edit on Amazon </span>
                <i class="fas fa-external-link-alt"></i>
                 ]
              </a>
              <span>
                <ng-container *ngIf="error.sku; else rawMessage">
                  Price {{ error.message.split('Price')[1]?.split('for')[0] || '' }} for <span class="sku-val">{{ error.sku }}</span> {{ error.message.split(error.sku)[1] || error.message }}
                </ng-container>
                <ng-template #rawMessage>{{ error.message }}</ng-template>
              </span>
            </div>
          </li>
        </ul>
      </div>

      <div class="error-footer" *ngIf="reportLink || hintMessage || uploadBatchId">
        <p *ngIf="hintMessage" class="hint-message">{{ hintMessage }}</p>
        <div class="error-footer-actions">
          <a *ngIf="reportLink" href="javascript:" (click)="downloadErrors()" class="download-link">
            <i class="fas fa-download"></i>Download full error report
          </a>
          <button *ngIf="uploadBatchId" type="button" class="btn continue-btn" (click)="onContinueToReview()">
            <i class="fas fa-arrow-right mr-1"></i>Continue to review
          </button>
        </div>
      </div>
    </ng-container>

    <ng-template #loadingTpl>
      <div class="content-card text-center py-3">
        <i class="fas fa-spinner fa-spin"></i> Loading errors...
      </div>
    </ng-template>
  </div>

</div>
